import React, { useState, useEffect, useRef } from 'react';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { animalApi, farmApi, stableApi, boxApi, animalFormOptionsApi } from '../services/api';
import { Farm, Stable, Box, MasterDataValue } from '../types';
import ClearableInput from './ClearableInput';
import ClearableSelect from './ClearableSelect';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';

interface ExportFilters {
  search?: string;
  status?: string;
  gender?: string;
  breed?: string;
  farm_id?: number;
  stable_id?: number;
  box_ids?: number[];
  cattle_passport_status?: string;
}

interface ExportAnimalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialFilters?: ExportFilters;
  initialFarmId?: number | null;
}

const SORT_FIELDS = [
  { value: 'ear_tag', label: 'Fülszám' },
  { value: 'breed', label: 'Fajta' },
  { value: 'gender', label: 'Ivar' },
  { value: 'birth_date', label: 'Születési dátum' },
  { value: 'status', label: 'Státusz' },
  { value: 'purchase_weight', label: 'Beszerzési súly' },
  { value: 'arrival_date', label: 'Érkezés dátuma' },
];

const EXPORT_COLUMNS = [
  { key: 'ear_tag', label: 'Fülszám' },
  { key: 'breed', label: 'Fajta' },
  { key: 'gender', label: 'Ivar' },
  { key: 'birth_date', label: 'Születési dátum' },
  { key: 'status', label: 'Státusz' },
  { key: 'purchase_weight', label: 'Beszerzési súly (kg)' },
  { key: 'price_per_kg', label: 'Beszerzési Ft/kg ár' },
  { key: 'arrival_date', label: 'Érkezés dátuma' },
  { key: 'origin_country', label: 'Származási ország' },
  { key: 'mother_ear_tag', label: 'Anya fülszáma' },
  { key: 'seller', label: 'Eladó' },
  { key: 'invoice_number', label: 'Beszerzési számla' },
  { key: 'notes', label: 'Megjegyzés' },
  { key: 'farm_name', label: 'Telep' },
  { key: 'stable_name', label: 'Istálló' },
  { key: 'box_name', label: 'Box' },
];

const ALL_COLUMN_KEYS = EXPORT_COLUMNS.map(c => c.key);

const ExportAnimalsModal: React.FC<ExportAnimalsModalProps> = ({
  isOpen,
  onClose,
  initialFilters = {},
  initialFarmId = null,
}) => {
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ExportFilters>({});
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);

  // Sort state
  const [sortBy, setSortBy] = useState('ear_tag');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Column picker state — all ON by default
  const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>(
    () => Object.fromEntries(ALL_COLUMN_KEYS.map(k => [k, true]))
  );

  // Reference data
  const [farms, setFarms] = useState<Farm[]>([]);
  const [stables, setStables] = useState<Stable[]>([]);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [breeds, setBreeds] = useState<MasterDataValue[]>([]);

  // Loading state
  const [exporting, setExporting] = useState(false);

  // Track whether we've already initialized for this open session
  const wasOpen = useRef(false);

  // Initialize filters from Animals page only on modal open (not on every parent re-render)
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      wasOpen.current = true;
      setFilters({
        status: initialFilters.status,
        gender: initialFilters.gender,
        breed: initialFilters.breed,
        stable_id: initialFilters.stable_id,
        box_ids: initialFilters.box_ids ?? [],
        cattle_passport_status: initialFilters.cattle_passport_status,
      });
      setSearchTerm(initialFilters.search || '');
      setSelectedFarmId(initialFarmId ?? null);
      setSelectedColumns(Object.fromEntries(ALL_COLUMN_KEYS.map(k => [k, true])));
    } else if (!isOpen) {
      wasOpen.current = false;
    }
  }, [isOpen, initialFilters, initialFarmId]);

  // Load reference data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadRefData = async () => {
      try {
        const [farmsData, breedsData] = await Promise.all([
          farmApi.getAll(),
          animalFormOptionsApi.getBreeds(),
        ]);
        setFarms(farmsData);
        setBreeds(breedsData || []);
      } catch (error) {
        console.error('Error loading reference data:', error);
        handleApiError(error, 'Hiba történt a referencia adatok betöltésekor');
      }
    };
    loadRefData();
  }, [isOpen]);

  // Load stables and boxes when farm changes
  useEffect(() => {
    if (!selectedFarmId) {
      setStables([]);
      setBoxes([]);
      return;
    }

    const loadLocationData = async () => {
      try {
        const [stablesData, boxesData] = await Promise.all([
          stableApi.getAll(selectedFarmId),
          boxApi.getAll(selectedFarmId),
        ]);
        setStables(stablesData);
        setBoxes(boxesData);
      } catch (error) {
        console.error('Error loading stables/boxes:', error);
        handleApiError(error, 'Hiba történt az istállók/boxok betöltésekor');
      }
    };
    loadLocationData();
  }, [selectedFarmId]);

  const filteredBoxOptions = filters.stable_id
    ? boxes.filter(b => b.stable_id === filters.stable_id)
    : boxes;

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: Record<string, any> = {};

      if (searchTerm) params.search = searchTerm;
      if (filters.status) params.status = filters.status;
      if (filters.gender) params.gender = filters.gender;
      if (filters.breed) params.breed = filters.breed;
      if (selectedFarmId) params.farm_id = selectedFarmId;
      if (filters.stable_id) params.stable_id = filters.stable_id;
      if (filters.box_ids?.length) params.box_ids = filters.box_ids;
      if (filters.cattle_passport_status) params.cattle_passport_status = filters.cattle_passport_status;
      params.sort_by = sortBy;
      params.sort_direction = sortDirection;

      // Send selected columns (only if not all selected)
      const activeKeys = ALL_COLUMN_KEYS.filter(k => selectedColumns[k]);
      if (activeKeys.length < ALL_COLUMN_KEYS.length) {
        params.columns = activeKeys;
      }

      const { blob, filename, exportCount } = await animalApi.exportAnimals(params);

      // Check for empty export via server-provided count
      if (exportCount === 0) {
        showError('A szűrési feltételeknek nem felel meg egyetlen állat sem. Az export fájl üres.');
      }

      // Trigger download via temporary link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('Exportálás sikeres!');
    } catch (error) {
      console.error('Export error:', error);
      handleApiError(error, 'Hiba történt az exportálás során.');
    } finally {
      setExporting(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({ box_ids: [] });
    setSearchTerm('');
    setSelectedFarmId(null);
    setSortBy('ear_tag');
    setSortDirection('asc');
    setSelectedColumns(Object.fromEntries(ALL_COLUMN_KEYS.map(k => [k, true])));
  };

  const selectedCount = ALL_COLUMN_KEYS.filter(k => selectedColumns[k]).length;

  const toggleColumn = (key: string) => {
    setSelectedColumns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleAllColumns = () => {
    const allOn = selectedCount === ALL_COLUMN_KEYS.length;
    setSelectedColumns(Object.fromEntries(ALL_COLUMN_KEYS.map(k => [k, !allOn])));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Állatok exportálása</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="px-6 py-6 overflow-y-auto flex-1">
          {/* Filters */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Szűrők</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Keresés</label>
                <ClearableInput
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Fülszám vagy fajta..."
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Telep</label>
                <ClearableSelect
                  value={String(selectedFarmId || '')}
                  onChange={(val) => {
                    setSelectedFarmId(val ? parseInt(val) : null);
                    setFilters(prev => ({ ...prev, stable_id: undefined, box_ids: [] }));
                  }}
                  placeholder="Összes telep"
                >
                  {farms.map(farm => (
                    <option key={farm.id} value={farm.id}>{farm.name}</option>
                  ))}
                </ClearableSelect>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Istálló</label>
                <ClearableSelect
                  value={String(filters.stable_id || '')}
                  onChange={(val) => setFilters({ ...filters, stable_id: val ? parseInt(val) : undefined, box_ids: [] })}
                  placeholder="Összes istálló"
                  disabled={!selectedFarmId}
                >
                  {stables.map(stable => (
                    <option key={stable.id} value={stable.id}>{stable.name}</option>
                  ))}
                </ClearableSelect>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1.5">Box</label>
                <div className="relative">
                  <Listbox
                    value={filters.box_ids ?? []}
                    onChange={(ids: number[]) => setFilters(prev => ({ ...prev, box_ids: ids }))}
                    multiple
                    disabled={!filters.stable_id}
                  >
                    <ListboxButton className="w-full h-10 flex items-center text-left border border-gray-300 rounded-md px-3 pr-8 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-400 overflow-hidden">
                      {(filters.box_ids?.length ?? 0) === 0 ? (
                        <span className="text-gray-400">
                          {!filters.stable_id ? 'Először válasszon istállót' : 'Összes box'}
                        </span>
                      ) : (filters.box_ids?.length ?? 0) <= 3 ? (
                        <div className="flex flex-nowrap gap-1 overflow-hidden">
                          {(filters.box_ids ?? []).map(id => {
                            const box = filteredBoxOptions.find(b => b.id === id);
                            return box ? (
                              <span key={id} className="inline-flex items-center bg-green-100 text-green-800 text-xs px-1.5 py-0.5 rounded">
                                {box.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-900">{filters.box_ids?.length} box kiválasztva</span>
                      )}
                      <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </ListboxButton>
                    <ListboxOptions
                      anchor="bottom start"
                      className="z-[200] w-[var(--button-width)] rounded-md bg-white shadow-lg border border-gray-200 overflow-auto focus:outline-none"
                    >
                      {filteredBoxOptions.map(box => (
                        <ListboxOption
                          key={box.id}
                          value={box.id}
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer text-sm text-gray-700 select-none data-[focus]:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={filters.box_ids?.includes(box.id) ?? false}
                            readOnly
                            className="rounded border-gray-300 text-green-600 pointer-events-none"
                          />
                          <span className="data-[selected]:font-medium">{box.name}</span>
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Listbox>
                  {(filters.box_ids?.length ?? 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => setFilters(prev => ({ ...prev, box_ids: [] }))}
                      className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      title="Kijelölés törlése"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Státusz</label>
                <ClearableSelect
                  value={filters.status || ''}
                  onChange={(val) => setFilters({ ...filters, status: val || undefined })}
                  placeholder="Összes státusz"
                >
                  <option value="aktiv">Aktív</option>
                  <option value="eladott">Eladott</option>
                  <option value="levagott">Levágott</option>
                  <option value="elhullott">Elhullott</option>
                </ClearableSelect>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Ivar</label>
                <ClearableSelect
                  value={filters.gender || ''}
                  onChange={(val) => setFilters({ ...filters, gender: val || undefined })}
                  placeholder="Összes ivar"
                >
                  <option value="him">Hím</option>
                  <option value="nosteny">Nőstény</option>
                </ClearableSelect>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Fajta</label>
                <ClearableSelect
                  value={filters.breed || ''}
                  onChange={(val) => setFilters({ ...filters, breed: val || undefined })}
                  placeholder="Összes fajta"
                >
                  {breeds.map(breed => (
                    <option key={breed.key} value={breed.value}>{breed.display_value || breed.value}</option>
                  ))}
                </ClearableSelect>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Marhalevél státusz</label>
                <ClearableSelect
                  value={filters.cattle_passport_status || ''}
                  onChange={(val) => setFilters({ ...filters, cattle_passport_status: val || undefined })}
                  placeholder="Összes"
                >
                  <option value="complete">Teljes (2/2)</option>
                  <option value="incomplete">Hiányos (0-1/2)</option>
                  <option value="missing">Hiányzik (0/2)</option>
                </ClearableSelect>
              </div>
            </div>
          </div>

          {/* Sort options */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Rendezés</h3>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">Rendezési mező</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                >
                  {SORT_FIELDS.map(field => (
                    <option key={field.value} value={field.value}>{field.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-shrink-0">
                <label className="block text-sm text-gray-600 mb-1">Irány</label>
                <button
                  type="button"
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className="border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50 transition-colors flex items-center gap-2"
                  title={sortDirection === 'asc' ? 'Növekvő' : 'Csökkenő'}
                >
                  {sortDirection === 'asc' ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                      Növekvő
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      Csökkenő
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Column picker */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">Oszlopok ({selectedCount}/{ALL_COLUMN_KEYS.length})</h3>
              <button
                type="button"
                onClick={toggleAllColumns}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                {selectedCount === ALL_COLUMN_KEYS.length ? 'Összes kijelölés törlése' : 'Összes kijelölése'}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {EXPORT_COLUMNS.map(col => (
                <label
                  key={col.key}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 rounded px-2 py-1"
                >
                  <input
                    type="checkbox"
                    checked={!!selectedColumns[col.key]}
                    onChange={() => toggleColumn(col.key)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  {col.label}
                </label>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Szűrők törlése
            </button>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Mégse
              </button>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || selectedCount === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Exportálás...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Exportálás (Excel)
                  </>
                )}
              </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ExportAnimalsModal;
